import React, { useEffect, useState } from 'react';
import { Save, Server, User, Sparkles } from 'lucide-react';
import { sapi } from '../../lib/api';
import { Spinner, useToast, Toast } from '../../components/UI';

const EMPTY = { smtpHost: '', smtpPort: '587', smtpUser: '', smtpPass: '', smtpFrom: '', smtpFromName: '', yourName: '', groqApiKey: '', aiCvMatchMode: 'fast' };

export default function StudentSettingsPage(): React.ReactElement {
  const { toast, show } = useToast();
  const [settings, setSettings] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    sapi.getStudentSettings().then((s: any) => { if (s) setSettings({ ...EMPTY, ...s }); }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const upd = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setSettings(s => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try { await sapi.saveStudentSettings(settings); show('Settings saved', 'success'); }
    catch (e: any) { show(e.message, 'error'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="page" style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}><Spinner size={28} /></div>;

  return (
    <div className="page fade-in">
      {toast && <Toast msg={toast.msg} type={toast.type} />}
      <div className="page-header">
        <div><h1 className="page-title">Student Settings</h1><p className="page-sub">Configure your personal email for job applications</p></div>
      </div>

      {/* Your name */}
      <div className="settings-section" style={{ marginBottom: 20 }}>
        <div className="settings-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><User size={15} /> Your Name</div>
        <div className="settings-section-desc">Used in cover letter sign-off ("Yours sincerely, …") and placeholders</div>
        <div className="form-group" style={{ maxWidth: 360, marginBottom: 0 }}>
          <label>Full Name</label>
          <input placeholder="John Smith" value={settings.yourName} onChange={upd('yourName')} />
        </div>
      </div>

      {/* AI Application */}
      <div className="settings-section" style={{ marginBottom: 20 }}>
        <div className="settings-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Sparkles size={15} /> AI Application</div>
        <div className="settings-section-desc">
          Powers the AI Application tab, which reads a pasted job description and drafts your application from it.
          Get a free key at <a href="https://console.groq.com/keys" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>console.groq.com/keys</a>.
        </div>
        <div className="form-group" style={{ maxWidth: 360 }}>
          <label>Groq API Key</label>
          <input type="password" placeholder="gsk_••••••••••••••••" value={settings.groqApiKey} onChange={upd('groqApiKey')} />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>CV Matching</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {([
              { v: 'fast', label: 'Fast', desc: 'Matches by CV name/category only' },
              { v: 'smart', label: 'Smart', desc: 'Reads each CV’s content to match more accurately' },
            ] as const).map(opt => {
              const active = settings.aiCvMatchMode === opt.v;
              return (
                <button key={opt.v} type="button" onClick={() => setSettings(s => ({ ...s, aiCvMatchMode: opt.v }))}
                  style={{ flex: 1, textAlign: 'left', padding: '10px 12px', borderRadius: 9, cursor: 'pointer', border: `1px solid ${active ? 'var(--accent)' : 'var(--border2)'}`, background: active ? 'rgba(108,99,255,0.08)' : 'transparent' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: active ? 'var(--accent)' : 'var(--text)' }}>{opt.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{opt.desc}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* SMTP */}
      <div className="settings-section">
        <div className="settings-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Server size={15} /> Email (SMTP)</div>
        <div className="settings-section-desc">Your personal Gmail or any SMTP provider. This is the email used to send job applications.</div>
        <div className="form-row">
          <div className="form-group"><label>SMTP Host</label><input placeholder="smtp.gmail.com" value={settings.smtpHost} onChange={upd('smtpHost')} /></div>
          <div className="form-group"><label>Port</label><input placeholder="587" value={settings.smtpPort} onChange={upd('smtpPort')} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Username</label><input placeholder="you@gmail.com" value={settings.smtpUser} onChange={upd('smtpUser')} /></div>
          <div className="form-group"><label>App Password</label><input type="password" placeholder="••••••••••••" value={settings.smtpPass} onChange={upd('smtpPass')} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>From Email</label><input placeholder="you@gmail.com" value={settings.smtpFrom} onChange={upd('smtpFrom')} /></div>
          <div className="form-group"><label>From Name</label><input placeholder="John Smith" value={settings.smtpFromName} onChange={upd('smtpFromName')} /></div>
        </div>
        <div style={{ background: 'rgba(108,99,255,0.06)', border: '1px solid rgba(108,99,255,0.15)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--text2)', marginBottom: 20 }}>
          💡 For Gmail: use an <strong style={{ color: 'var(--text)' }}>App Password</strong> — Google Account → Security → 2FA → App Passwords
        </div>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? <Spinner size={14} /> : <Save size={14} />} Save Settings
        </button>
      </div>
    </div>
  );
}
